import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, Zap, IndianRupee, Calendar, ArrowRight, CheckCircle2, Sparkles, Calculator } from "lucide-react";
import { Toaster, toast } from "sonner";
import api, { formatApiError } from "../lib/api";

// ── Solar India estimator (PM Surya Ghar Muft Bijli Yojana 2024+) ──────
function estimate({ monthlyBill, propertyType }) {
  const tariff = 8; // ₹/unit average residential
  const monthlyUnits = Math.max(0, monthlyBill / tariff);
  const kwRaw = monthlyUnits / 120; // ~120 units/kW/month India avg
  const kw = Math.max(1, Math.round(kwRaw));
  const areaSqft = kw * 100;
  const systemCost = kw * 60000; // ₹60k/kW residential turnkey
  // Subsidy slab (residential only): 1kW=₹30k, 2kW=₹60k, 3kW+ = ₹78k. None for commercial/HS.
  let subsidy = 0;
  if (propertyType === "residential") {
    if (kw >= 3) subsidy = 78000;
    else if (kw === 2) subsidy = 60000;
    else if (kw === 1) subsidy = 30000;
  } else if (propertyType === "housing_society") {
    subsidy = Math.min(kw, 500) * 18000; // group housing slab
  }
  const netCost = Math.max(0, systemCost - subsidy);
  const monthlySavings = Math.round(monthlyBill * 0.9);
  const annualSavings = monthlySavings * 12;
  const paybackYears = annualSavings > 0 ? netCost / annualSavings : 0;
  return { tariff, monthlyUnits, kw, areaSqft, systemCost, subsidy, netCost, monthlySavings, annualSavings, paybackYears };
}

const PROPERTY_TYPES = [
  { value: "residential", label: "Residential", sub: "Up to ₹78k subsidy" },
  { value: "commercial", label: "Commercial", sub: "No subsidy" },
  { value: "housing_society", label: "Housing Society", sub: "Group housing slab" },
];

const BILL_PRESETS = [1500, 3000, 5000, 8000, 15000];

function fmt(n) { return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 }); }

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-[#E2E4DC] bg-white p-4">
      <div className="flex items-center gap-2 text-[#4A5568]">
        <Icon className="w-4 h-4" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{label}</span>
      </div>
      <div className={"font-display font-bold mt-1.5 leading-none " + (accent ? "text-[#FF5C00] text-3xl" : "text-[#0A1128] text-2xl")}>
        {value}
      </div>
    </div>
  );
}

export default function PublicQuote() {
  const [step, setStep] = useState(1);
  const [bill, setBill] = useState(5000);
  const [property, setProperty] = useState("residential");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const est = useMemo(() => estimate({ monthlyBill: Number(bill), propertyType: property }), [bill, property]);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name, phone: form.phone, email: form.email.trim().toLowerCase(),
        address: form.address, customer_type: property,
        monthly_bill: Number(bill),
        kw_size: est.kw, system_cost: est.systemCost, subsidy: est.subsidy,
        monthly_savings: est.monthlySavings, payback_years: est.paybackYears,
        avg_power_kwh: est.monthlyUnits,
      };
      const { data } = await api.post("/public/quote-lead", payload);
      setDone(data);
      toast.success("Lead captured! Our team will call within 24h.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  }

  // ── DONE SCREEN ──────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-[#F4F5F0] crm-grid">
        <div className="mobile-frame bg-white" data-testid="quote-success">
          <div className="px-6 pt-12 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#D4FF00] mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#0A1128]" strokeWidth={2.5} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#4A5568] mt-6">Hot lead · Captured</div>
            <h1 className="font-display text-4xl font-bold tracking-tighter leading-none mt-3">You're on the grid.</h1>
            <p className="text-sm text-[#4A5568] mt-4 max-w-sm mx-auto">
              {done.message} We've earmarked a <span className="text-[#0A1128] font-semibold">{est.kw} kW</span> system saving you{" "}
              <span className="text-[#FF5C00] font-semibold">₹{fmt(est.monthlySavings)}/month</span>.
            </p>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-8">Reference</div>
            <div className="font-mono text-xs mt-1 text-[#0A1128]">{done.lead_id.slice(0, 8).toUpperCase()}</div>

            <div className="mt-10 space-y-2">
              <Link to="/login" data-testid="post-quote-login"
                className="block w-full bg-[#FF5C00] text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#0A1128] transition-colors">
                Sign in to track lifecycle
              </Link>
              <Link to="/signup" className="block w-full border border-[#0A1128] py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#0A1128] hover:text-white transition-colors">
                Create a customer account
              </Link>
            </div>
          </div>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // ── MAIN UI ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F4F5F0]">
      <div className="mobile-frame bg-white" data-testid="quote-page">
        {/* Hero */}
        <div className="relative overflow-hidden bg-[#0A1128] text-white">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#FF5C00] opacity-40 blur-3xl"></div>
          <div className="absolute right-12 -bottom-12 w-56 h-56 rounded-full bg-[#D4FF00] opacity-30 blur-3xl"></div>
          <div className="relative px-6 pt-8 pb-10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#FF5C00] flex items-center justify-center">
                <Sun className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display text-base leading-none font-bold">Solar.</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#94A3B8] mt-0.5">Free Estimator</div>
              </div>
              <Link to="/login" className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] hover:text-[#D4FF00]">
                Sign in →
              </Link>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] mt-8">60-second rooftop quote</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.9] mt-3">
              Cut your electric<br />bill by{" "}
              <span className="italic font-light text-[#D4FF00]">90%.</span>
            </h1>
            <p className="text-sm text-[#94A3B8] mt-4 leading-relaxed">
              Real numbers — system size, subsidy, monthly savings, payback. No login.
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex border-b border-[#E2E4DC]">
          {[1, 2].map((i) => (
            <div key={i} className={"flex-1 px-5 py-3 font-mono text-[10px] uppercase tracking-widest border-b-2 " +
              (step >= i ? "border-[#FF5C00] text-[#0A1128]" : "border-transparent text-[#94A3B8]")}>
              <div className="text-[9px] opacity-70">Step 0{i}</div>
              <div>{i === 1 ? "Estimate" : "Get callback"}</div>
            </div>
          ))}
        </div>

        {/* STEP 1 — Estimator */}
        {step === 1 && (
          <div className="px-6 py-6 space-y-6" data-testid="quote-step-1">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A5568]">Property type</div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {PROPERTY_TYPES.map((p) => (
                  <button key={p.value} onClick={() => setProperty(p.value)}
                    data-testid={`prop-${p.value}`}
                    className={"p-3 text-left border transition-colors " +
                      (property === p.value ? "border-[#FF5C00] bg-[#FF5C00]/5" : "border-[#E2E4DC] hover:border-[#0A1128]")}>
                    <div className="font-display text-sm font-bold leading-tight">{p.label}</div>
                    <div className="font-mono text-[9px] text-[#4A5568] mt-1 leading-tight">{p.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A5568]">Monthly electric bill</div>
                <div className="font-display text-2xl font-bold text-[#FF5C00]">₹{fmt(bill)}</div>
              </div>
              <input
                type="range" min="500" max="50000" step="500" value={bill}
                onChange={(e) => setBill(Number(e.target.value))}
                data-testid="bill-slider"
                className="w-full mt-3 accent-[#FF5C00]"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {BILL_PRESETS.map((p) => (
                  <button key={p} onClick={() => setBill(p)}
                    className={"text-[10px] font-mono px-2.5 py-1 border " +
                      (bill === p ? "bg-[#0A1128] text-white border-[#0A1128]" : "border-[#E2E4DC] text-[#4A5568] hover:border-[#0A1128]")}>
                    ₹{fmt(p)}
                  </button>
                ))}
              </div>
            </div>

            {/* Live results */}
            <div className="bg-[#0A1128] text-white p-5 relative overflow-hidden">
              <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-[#FF5C00]/30 blur-2xl"></div>
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#D4FF00]" /> Your estimate
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <div className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">System size</div>
                    <div className="font-display text-5xl font-black text-[#D4FF00] mt-1 leading-none">{est.kw}<span className="text-xl text-white ml-1">kW</span></div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">Roof needed</div>
                    <div className="font-display text-3xl font-bold mt-1 leading-none">{fmt(est.areaSqft)}<span className="text-sm text-[#94A3B8] ml-1 font-mono">sqft</span></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#1E293B]">
                  <div>
                    <div className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">System cost</div>
                    <div className="font-display text-xl font-bold mt-1">₹{fmt(est.systemCost)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">Govt. subsidy</div>
                    <div className="font-display text-xl font-bold mt-1 text-[#D4FF00]">−₹{fmt(est.subsidy)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">Net cost</div>
                    <div className="font-display text-xl font-bold mt-1 text-[#FF5C00]">₹{fmt(est.netCost)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">Payback</div>
                    <div className="font-display text-xl font-bold mt-1">{est.paybackYears.toFixed(1)}<span className="text-xs ml-1 font-mono">yrs</span></div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-[#1E293B] text-center">
                  <div className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">Save every month</div>
                  <div className="font-display text-5xl font-black text-[#D4FF00] mt-1">₹{fmt(est.monthlySavings)}</div>
                  <div className="font-mono text-xs text-[#94A3B8] mt-1">≈ ₹{fmt(est.annualSavings)} per year</div>
                </div>
              </div>
            </div>

            <button onClick={() => setStep(2)} data-testid="quote-next"
              className="btn-tactile w-full bg-[#FF5C00] text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#0A1128] transition-colors flex items-center justify-center gap-2">
              Lock in this quote <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[10px] font-mono text-[#94A3B8] text-center">
              Indicative numbers · Final design via free site survey · No spam
            </p>
          </div>
        )}

        {/* STEP 2 — Capture */}
        {step === 2 && (
          <form onSubmit={submit} className="px-6 py-6 space-y-5" data-testid="quote-form">
            <div className="bg-[#F4F5F0] p-4 grid grid-cols-3 gap-3">
              <StatCard icon={Zap} label="kW" value={est.kw} />
              <StatCard icon={IndianRupee} label="Monthly save" value={`₹${fmt(est.monthlySavings)}`} accent />
              <StatCard icon={Calendar} label="Payback" value={`${est.paybackYears.toFixed(1)}y`} />
            </div>

            <div>
              <div className="font-display text-2xl font-bold tracking-tighter">Where shall we call you?</div>
              <p className="text-sm text-[#4A5568] mt-1">A solar consultant will reach out within 24h with a custom design.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#4A5568] mb-1.5">Full name *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="quote-name"
                  className="w-full bg-white border border-[#E2E4DC] focus:border-[#FF5C00] outline-none px-3 py-3 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#4A5568] mb-1.5">Phone *</label>
                <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="quote-phone" placeholder="+91-9XXXXXXXXX"
                  className="w-full bg-white border border-[#E2E4DC] focus:border-[#FF5C00] outline-none px-3 py-3 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#4A5568] mb-1.5">Email *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="quote-email" placeholder="you@email.com"
                  className="w-full bg-white border border-[#E2E4DC] focus:border-[#FF5C00] outline-none px-3 py-3 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#4A5568] mb-1.5">Property address</label>
                <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="quote-address" placeholder="City + locality is enough"
                  className="w-full bg-white border border-[#E2E4DC] focus:border-[#FF5C00] outline-none px-3 py-3 text-sm font-mono" />
              </div>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)}
                className="border border-[#0A1128] px-4 py-3 font-mono text-[10px] uppercase tracking-widest hover:bg-[#0A1128] hover:text-white transition-colors">
                ← Back
              </button>
              <button type="submit" disabled={submitting} data-testid="quote-submit"
                className="btn-tactile flex-1 bg-[#FF5C00] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#0A1128] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {submitting ? "Submitting…" : (<>Get my callback <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </div>
            <p className="text-[10px] font-mono text-[#94A3B8] text-center">
              We'll only use these to share your custom solar design.
            </p>
          </form>
        )}

        {/* footer */}
        <div className="px-6 py-6 border-t border-[#E2E4DC] flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
          <div className="flex items-center gap-1.5"><Calculator className="w-3 h-3" /> India · 2026 tariff</div>
          <Link to="/" className="hover:text-[#FF5C00]">Home →</Link>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
